import { useState } from "react"
import type { ApolloError } from "@apollo/client"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import LicenseSeatLimitFields from "@/v2-backoffice-client/license/seats/LicenseSeatLimitFields"
import { useLicenseSeatLimitMutation } from "@/v2-backoffice-client/license/seats/license.seats.limit.mutation"
import {
  EMPTY_SEAT_LIMIT_DRAFT,
  type LicenseSeatPerson,
  isSeatLimitCountValid,
  seatLimitCountOf,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import { evictLicenseSeatQueries } from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_SOURCE_OPERATOR } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseSeatLimitFormProps = {
  license: LicenseFragment
  people: readonly LicenseSeatPerson[]
  isLoading: boolean
  error?: ApolloError
  onLimited(): void
}

// The one step from "everyone at the customer" to "these people" (FUS-2766):
// tick the people, the seat count follows them unless typed, one save.
// Cannot be saved with nobody ticked, so a license is never limited to
// nobody. Afterwards the Seats dialog shows the Limited mode, where names
// and count are changed one at a time.
function LicenseSeatLimitForm(props: LicenseSeatLimitFormProps) {
  const { license, people, isLoading, error, onLimited } = props
  const [draft, setDraft] = useState(EMPTY_SEAT_LIMIT_DRAFT)

  const isOperator = license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
  const customerName = license.CustomerName ?? "the customer"
  const picked = draft.userIds.length
  const seatCount = seatLimitCountOf(draft, isOperator)

  const [limitSeats, { loading }] = useLicenseSeatLimitMutation({
    update: evictLicenseSeatQueries,
    onCompleted(data) {
      const updated = data.setLicenseSeatLimit
      toast({
        variant: "success",
        title: "Seats limited",
        description: `Only the ${updated.SeatsAssigned} named ${updated.SeatsAssigned === 1 ? "person" : "people"} at ${updated.CustomerName ?? "the customer"} have ${updated.ProductName} now${updated.Seats != null ? `, on ${updated.Seats} paid ${updated.Seats === 1 ? "seat" : "seats"}` : ""}.`,
      })
      onLimited()
    },
    onError(error) {
      toast({
        variant: "destructive",
        title: "Seats not limited",
        description: error.message,
      })
    },
  })

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <LicenseSeatLimitFields
        people={people}
        isLoading={isLoading}
        error={error}
        customerName={customerName}
        draft={draft}
        onDraftChange={setDraft}
        seatsFollowPicked={isOperator}
        billingSeats={license.BillingSeats}
        idPrefix="licenseSeatLimit"
      />

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground">
          {picked === 0
            ? "Tick at least one person."
            : `After saving, only the ${picked} ${picked === 1 ? "person" : "people"} ticked ${picked === 1 ? "has" : "have"} this license.`}
        </p>
        <Button
          type="button"
          isLoading={loading}
          disabled={picked === 0 || !isSeatLimitCountValid(draft)}
          data-feature-code="UI_BackofficeLicense_Seats_Limit"
          onClick={() => {
            void limitSeats({
              variables: {
                licenseId: license.Id,
                input: {
                  UserIds: draft.userIds,
                  Seats: seatCount ?? undefined,
                },
              },
            })
          }}
        >
          Limit Seats
        </Button>
      </div>
    </div>
  )
}

export default LicenseSeatLimitForm
